
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@/components/Table';
import { Badge } from '@/components/Badge';
import { date } from '@/utils/format';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { tasksApi } from '@/lib/api';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { Tooltip } from '@/components/Tooltip';
import { Avatar } from '@/components/Avatar';
import type { BadgeVariant } from '@/components/Badge';
import { ChangeTaskPriorityModal } from '@/pages/Tasks/ChangeTaskPriorityModal';
import { ChangeTaskStatusModal } from '@/pages/Tasks/ChangeTaskStatusModal';
import { ChangeTaskAssigneeModal } from '@/pages/Tasks/ChangeTaskAssigneeModal';
import { ChangeTaskTypeModal } from '@/pages/Tasks/ChangeTaskTypeModal';
import { ListSettings } from '@/components/ListSettings';
import { ListPanel } from '@/components/ListPanel';
import { SavedViews } from '@/components/SavedViews';
import { listViewSchema } from '@/components/listPreferences';
import { useLocalList } from '@/hooks/useLocalList';

import { PRIORITY_CONFIG } from '@/components/PrioritySelect';

const TASK_STATUS_VARIANT: Record<string, BadgeVariant> = {
    'todo': 'neutral',
    'progress': 'info',
    'review': 'warning',
    'done': 'success',
    'cancelled': 'danger'
};

interface TasksTableProps {
    tasks: any[];
    search?: string;
    onSearchChange?: (val: string) => void;
    filters?: React.ReactNode;
    onRowClick?: (row: any) => void;
    onEditClick?: (row: any) => void;
    onCreateRelated?: (row: any, kind: 'subtask' | 'related') => void;
    onRefresh?: () => void;
    viewFilters?: Record<string, string>;
    viewContext?: string;
    onRestoreView?: (filters: Record<string, string>, context: string) => void;
    taskTypes?: any[];
}

export const TasksTable: React.FC<TasksTableProps> = ({ 
    tasks, 
    search,
    onSearchChange,
    filters,
    onRowClick,
    onEditClick,
    onCreateRelated,
    onRefresh,
    viewFilters,
    viewContext,
    onRestoreView,
    taskTypes = [],
}) => {
    const { t } = useTranslation('global');
    const { isIt } = useAuth();
    const { showSuccess, showError } = useToast();
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [priorityTarget, setPriorityTarget] = useState<any>(null);
    const [statusTarget, setStatusTarget] = useState<any>(null);
    const [assigneeTarget, setAssigneeTarget] = useState<any>(null);
    const [typeTarget, setTypeTarget] = useState<any>(null);
    const list = useLocalList({ key: 'vigepra_tasks', search: search || '', onSearch: value => onSearchChange?.(value), initialSort: { key: 'updated_at', order: 'desc' }, filters: viewFilters, context: viewContext, onRestore: onRestoreView, defaultFilters: { statuses: '[]', assignees: '[]' }, defaultContext: 'open' });
    const { preferences, setPreferences } = list;

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const res = await tasksApi.delete(deleteTarget.id);
            if (res.result) {
                showSuccess(t('tasks.deleted'));
                setDeleteTarget(null);
                if (onRefresh) onRefresh();
                else window.location.reload();
            } else {
                showError(res.message || t('errors.default'));
            }
        } catch (err) {
            showError(err instanceof Error ? err.message : t('errors.default'));
        } finally {
            setIsDeleting(false);
        }
    };

    const columns = useMemo(() => [
        {
            label: t('tasks.table.code'),
            key: 'code',
            sortable: true,
            render: (row) => (
                <Badge
                    value={row.code}
                />
            ),
        },
        {
            label: t('tasks.table.type'),
            key: 'type.name',
            sortable: false,
            align: 'center',
            render: (row) => {
                const type = row.type;
                if (!type) return <Badge value={t('tasks.table.empty')} />;
                const typeTag = type.tag === 'development' ? 'task' : type.tag;
                const key = `tasks.types.${typeTag}`;
                const translatedLabel = t(key);
                return (
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            setTypeTarget(row);
                        }}
                        className="cursor-pointer transition-opacity hover:opacity-80"
                    >
                        <Badge
                            icon={type.icon || (typeTag === 'bug' ? 'bug' : 'clipboardCheck')}
                            variant={typeTag === 'bug' ? 'danger' : 'info'}
                            value={translatedLabel !== key ? translatedLabel : type.name}
                        />
                    </button>
                );
            },
        },
        {
            label: t('tasks.table.updated_at'),
            key: 'updated_at',
            sortable: true,
            nowrap: true,
            render: (row) => (
                <span className="">
                    {date(row.updated_at)}
                </span>
            ),
        },
        {
            label: t('tasks.table.title'),
            key: 'title',
            sortable: true,
            render: (row) => (                
                <div className="flex flex-col py-0.5">
                    <Tooltip title={row.title} placement="top-start">
                        <span className="font-bold line-clamp-1">
                            {row.title}
                        </span>
                    </Tooltip>
                    <span className="text-xs text-slate-500 dark:text-zinc-400 line-clamp-1 mt-0.5">
                        {row.description}
                    </span>                                            
                </div>
            )
        },
        {
            label: t('tasks.table.status'),
            key: 'status.order',
            sortable: true,
            align: 'center',
            render: (row) => {
                const status = row.status;
                if (!status) return null;
                const statusTag = status.tag || 'todo';
                return (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setStatusTarget(row); }} className="hover:opacity-80 transition-opacity">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setTypeTarget(row); }} className="hover:opacity-80 transition-opacity">
                        <Badge
                            variant={TASK_STATUS_VARIANT[statusTag] ?? 'neutral'}
                            value={t(`tasks.statuses.${statusTag}`, { defaultValue: status.name || t('tasks.statuses.todo') })}
                        />
                        </button>
                    </button>
                );
            }
        },
        {
            label: t('tasks.table.priority'),
            key: 'priority',
            sortable: true,
            align: 'center',
            render: (row) => {
                const priorityKey = row.priority ?? 2;
                const priorityCfg = PRIORITY_CONFIG[priorityKey] || PRIORITY_CONFIG[2];
                return (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPriorityTarget(row); }} className="hover:opacity-80 transition-opacity cursor-pointer">
                        <Tooltip title={t(`priorities.${priorityCfg.tag}`)} placement="top">
                            <span className={`${priorityCfg.color}`}>
                                {Array.from({ length: priorityCfg.arrows }).map((_, idx) => (
                                    <Icon key={idx} value="arrowUp" size={16} strokeWidth={3} />
                                ))}
                            </span>
                        </Tooltip>
                    </button>
                );
            }
        },
        {
            label: t('tasks.table.development'),
            key: 'development.title',
            sortable: false,
            render: (row) => {
                const dev = row.development;
                if (!dev) return '';
                return (
                    <div className="flex flex-col py-0.5">
                        <span className="text-xs font-bold line-clamp-1">{dev.code}</span>
                        <Tooltip title={dev.title} placement="top-start">
                            <span className="text-xs text-slate-700 dark:text-zinc-300 line-clamp-1 truncate w-48 mt-0.5">
                                {dev.title}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            label: t('tasks.table.assignee'),
            key: 'assignee.name',
            sortable: false,
            nowrap: true,
            render: (row) => {
                const assignee = row.assignee;
                
                return (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setAssigneeTarget(row); }} className="hover:opacity-80 transition-opacity">
                        <Avatar
                            name={assignee?.name || '-'}
                            src={assignee?.avatar || assignee?.photo}
                            size="sm"
                        />
                    </button>
                );
            }
        },
    ], [t]);

    const tableActions = [
        ...(onCreateRelated ? [
            {
                label: t('tasks.actions.create_subtask'),
                icon: <Icon value="plus" size={14} className="text-slate-600 dark:text-zinc-300" />,
                onClick: (row: any): void => onCreateRelated(row, 'subtask'),
            },
            {
                label: t('tasks.actions.related_task'),
                icon: <Icon value="link" size={14} className="text-slate-600 dark:text-zinc-300" />,
                onClick: (row: any): void => onCreateRelated(row, 'related'),
            },
        ] : []),
        {
            label: t('table.edit'),
            icon: <Icon value="pencil" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: (row: any): void => {
                if(onEditClick){
                    onEditClick(row);
                }
                else{
                    if(onRowClick){
                        onRowClick(row);
                    }
                }
            },
        },
        ...(isIt ? [
            {
                label: t('table.delete'),
                icon: <Icon value="trash" size={14} className="text-rose-600" />,
                variant: 'delete' as const,
                onClick: (row: any): void => {
                    setDeleteTarget(row);
                },
            },
        ] : []),
    ];

    return (
        <>
            <Table
                appearance="workspace"
                sort={list.sort}
                onSortChange={list.setSort}
                pagination={list.pagination(tasks.length)}
                manualPagination={false}
                density={preferences.density}
                data={tasks}
                columns={columns.filter(column => !preferences.hidden.includes(column.key))}
                actions={tableActions}
                actionsPosition="right"
                actionMenu={true}
                onRowClick={onRowClick}
                paginated={true}
                searchable={true}
                filterbox={false}
                searchValue={search}
                onSearchChange={onSearchChange}
                filterElement={(
                    <div className="flex flex-wrap items-center gap-1.5">
                        {filters && <Button variant="outline" size="sm" icon="filter" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(previous => !previous)}>{t('providers.ui.filters')}</Button>}
                        <SavedViews storageKey="vigepra_task_views" schema={listViewSchema} value={list.view} defaultValue={list.defaultView} onApply={list.apply}
                            configuration={<ListSettings inline columns={columns} value={preferences} onChange={setPreferences} sort={list.sort} onSortChange={list.setSort} />} />
                    </div>
                )}
                defaultSortKey="updated_at"
                defaultSortOrder="desc"
                filterPanel={<ListPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('providers.ui.filters')}>
                    <div className="flex flex-wrap gap-3 [&>div]:!w-auto [&>div]:min-w-48 [&>div]:flex-1">{filters}</div>
                </ListPanel>}
            />

            {deleteTarget && (
                <Modal
                    isOpen={deleteTarget !== null}
                    onClose={() => {
                        if (!isDeleting) setDeleteTarget(null);
                    }}
                    title={t('tasks.delete_title')}
                    maxWidth="sm"
                    onSubmit={handleConfirmDelete}
                    submitLabel={t('modal.delete')}
                    submittingLabel={t('modal.deleting')}
                    cancelLabel={t('modal.cancel')}
                    isSubmitting={isDeleting}
                    submitIcon={<Icon value="trash" size={15} />}
                    variant="danger"
                >
                    <div className="flex flex-col items-center justify-center text-center p-4">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                            <Icon value="alertTriangle" size={32} className="text-red-600 dark:text-red-400" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 dark:text-zinc-100 mb-2">
                            {t('tasks.delete_desc')}
                        </h3>
                        <div className="text-sm text-slate-600 dark:text-zinc-400">
                            {deleteTarget?.code} - {deleteTarget?.title}
                        </div>
                        {/*
                        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-lg border border-red-100 dark:border-red-900/30 w-full">
                            <Icon value="info" size={16} className="inline mr-2 -mt-0.5" />
                            {t('tasks.delete_warning')}
                        </div>
                        */}
                    </div>
                </Modal>
            )}

            <ChangeTaskStatusModal
                isOpen={statusTarget !== null}
                task={statusTarget}
                onClose={() => setStatusTarget(null)}
                onSuccess={() => { setStatusTarget(null); if(onRefresh) onRefresh(); else window.location.reload(); }}
            />

            <ChangeTaskPriorityModal
                isOpen={priorityTarget !== null}
                task={priorityTarget}
                onClose={() => setPriorityTarget(null)}
                onSuccess={() => { setPriorityTarget(null); if(onRefresh) onRefresh(); else window.location.reload(); }}
            />
            {assigneeTarget && (
                <ChangeTaskAssigneeModal
                    isOpen={!!assigneeTarget}
                    onClose={() => setAssigneeTarget(null)}
                    task={assigneeTarget}
                    onSuccess={() => {
                        setAssigneeTarget(null);
                        if (onRefresh) onRefresh();
                        else window.location.reload();
                    }}
                />
            )}

            <ChangeTaskTypeModal
                isOpen={typeTarget !== null}
                task={typeTarget}
                types={taskTypes}
                onClose={() => setTypeTarget(null)}
                onSuccess={(updated: any) => {
                    setTypeTarget(null);
                    onRefresh?.();
                }}
            />
        </>
    );
};
